
import { Component } from "vue-property-decorator";
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import { HospitalData, ReservationData } from "@/@types/reservation";
import MemberService from "@/services/members";
import FacilityService from "@/services/facilities";
import AuthModule from "@/store/auth";
import { authCheck, deleteNullProp } from "@/utils/Util";
import CheckCommonLogin from "@/utils/CheckCommonLogin";
import Calendar from "@/components/Calender.vue";
import ReservationModule from "@/store/reservation";
import moment from "moment";
import _ from "lodash";
import Cognito from "@/plugins/cognito";
import japaneseHolidays from "japanese-holidays";

@Component({
  name: "MyPageReservationEdit",
  components: {
    Header,
    Footer,
    Calendar,
  },
})
export default class MyPageReservationEdit extends CheckCommonLogin {
  private get me() {
    return AuthModule.me;
  }

  private reservationData: ReservationData | null = null;

  private hospitalInfo: HospitalData | null = null;

  private pendingDate: string | null = null;

  private modalErrorMessage = "エラーが発生しました。";

  private async getHospitalInfo() {
    try {
      if (this.reservationData) {
        const res = await FacilityService.getHospital(this.reservationData?.facility_id);
        this.hospitalInfo = res.data.data[0];
      }
    } catch (error: any) {
      throw new Error(error);
    }
  }

  private async created() {
    await this.getReservationDetail();
    this.getHospitalInfo();
  }

  private loading = false;
  private submitLoading = false;

  private get isMessageRequired(): boolean {
    return this.reservationData?.is_message_required ?? false;
  }

  private async onSelectDate(date: string) {
    this.pendingDate = date;
    if (!this.isChangeable(this.reservationData)) {
      this.showModal("noChange");
      return;
    }
    if (this.isMessageRequired) {
      const result = await (this.$refs.messageProvider as any).validate();
      if (!result.valid) return;
    }
    this.showModal("confirm");
  }

  private reservationDateFactory(time: string) {
    return moment(new Date(time).toISOString()).format("YYYY/MM/DD(ddd)");
  }
  private reservationTimeFactory(time: string) {
    return moment(new Date(time).toISOString()).format("HH:mm");
  }

  private cancelChange() {
    ReservationModule.changeSelectedDate(null);
    this.$router.push({ name: "MyPage" });
  }

  private isChangeable(reservation: any) {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    let isChangeable = false;
    if (reservation.courseDetail) {
      const reservationDate = moment(new Date(reservation.datetime));
      const day = days[reservationDate.get("day")];
      const isHoliday = !!japaneseHolidays.isHoliday(reservationDate.toDate());
      const courseDaysData = reservation.courseDetail ? reservation.courseDetail.days : [];
      const timeObject = courseDaysData.find(
        (value: any) => value.available_days.includes(day) || (isHoliday && value.available_days.includes("Holiday"))
      );
      if (timeObject) {
        const endMonth = -timeObject.times[0].reception_time.end_month;
        const endDate = -timeObject.times[0].reception_time.end_date;
        const endHour = timeObject.times[0].reception_time.end_hour;
        // item.datetimeの月からendMonthを引いて、endDateも引いて時間はendHourをセットする
        const endReceptionTime = moment(new Date(reservation.datetime))
          .add(endMonth, "month")
          .add(endDate, "day")
          .set("hour", endHour)
          .format("YYYY-MM-DD HH:mm");
        // 現在時刻が受付時間を超えているかどうか
        isChangeable = moment(new Date()).isBefore(moment(endReceptionTime));
      }
      //変更可能かどうか
    }
    return isChangeable;
  }

  private async isChangeableCheck(reservation: any) {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    let isChangeable = false;
    const courseRes = await FacilityService.getHospitalCourseDetail(reservation.facility_id, reservation.course_id);
    const courseDetail = courseRes.data.data[0].details[0];
    if (courseDetail) {
      const reservationDate = moment(new Date(reservation.datetime));
      const day = days[reservationDate.get("day")];
      const isHoliday = !!japaneseHolidays.isHoliday(reservationDate.toDate());
      const courseDaysData = courseDetail ? courseDetail.days : [];
      const timeObject = courseDaysData.find(
        (value: any) => value.available_days.includes(day) || (isHoliday && value.available_days.includes("Holiday"))
      );
      if (timeObject) {
        const endMonth = -timeObject.times[0].reception_time.end_month;
        const endDate = -timeObject.times[0].reception_time.end_date;
        const endHour = timeObject.times[0].reception_time.end_hour;
        // item.datetimeの月からendMonthを引いて、endDateも引いて時間はendHourをセットする
        const endReceptionTime = moment(new Date(reservation.datetime))
          .add(endMonth, "month")
          .add(endDate, "day")
          .set("hour", endHour)
          .format("YYYY-MM-DD HH:mm");
        // 現在時刻が受付時間を超えているかどうか
        isChangeable = moment(new Date()).isBefore(moment(endReceptionTime));
      }
      //変更可能かどうか
    }
    return isChangeable;
  }

  private async getReservationDetail() {
    try {
      this.loading = true;
      const reservationId = this.$route.params.id;
      if (this.me && reservationId) {
        const res = await MemberService.getReservationDetail(this.me.id, reservationId);
        const reservationData = res.data.data[0];
        const hospitalRes = await FacilityService.getHospital(reservationData.facility_id);
        const courseRes = await FacilityService.getHospitalCourseDetail(
          reservationData.facility_id,
          reservationData.course_id
        );
        let petName = "";
        if (reservationData.pet_id) {
          const res = await MemberService.getPet(reservationData.member_id, reservationData.pet_id);
          petName = res.data.data[0].name;
        }
        // 予約日にマッチした Detail を取得して返す
        const details = courseRes.data.data[0] ? courseRes.data.data[0].details : [];
        const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const reservationDate = moment(new Date(reservationData.datetime)); // 予約日時をmomentで変換
        const day = days[reservationDate.get("day")]; // 予約日時の曜日を取得
        // 予約日時にマッチした Detail を取得
        const matchedDetail =
          details.find((detail: any) => {
            const p = detail.period;
            // 期間開始日
            const start = moment({ year: p.start_year, month: p.start_month - 1, day: p.start_date }).startOf("day");
            // 期間終了日（「終了日を設定しない」がチェックされている場合は end が period に存在しない）
            const hasEnd = p.end_year != null && p.end_month != null && p.end_date != null;
            // 期間内かどうか確認
            let inPeriod: boolean;
            if (hasEnd) {
              const end = moment({ year: p.end_year, month: p.end_month - 1, day: p.end_date }).endOf("day");
              // 期間内（start - end）
              inPeriod = reservationDate.isBetween(start, end, null, "[]");
            } else {
              // 終了日がない場合は開始日以降
              inPeriod = reservationDate.isSameOrAfter(start);
            }
            // 曜日一致（祝日の場合は "Holiday" を含むグループもマッチ）
            const isHoliday = !!japaneseHolidays.isHoliday(reservationDate.toDate());
            const hasDay = (detail.days ?? []).some(
              (v: any) => v.available_days?.includes(day) || (isHoliday && v.available_days?.includes("Holiday"))
            );
            return inPeriod && hasDay;
          }) ?? {};

        this.reservationData = {
          ...reservationData,
          hospitalName: hospitalRes.data.data[0].name,
          courseName: courseRes.data.data[0].name,
          courseDetail: matchedDetail,
          petName,
          is_message_required: courseRes.data.data[0].is_message_required ?? false,
        };
      }
      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      throw new Error(error);
    }
  }

  private async reservationDelete() {
    this.submitLoading = true;
    try {
      const isChangeable = await this.isChangeable(this.reservationData);
      if (!isChangeable) {
        this.closeModal("delete");
        this.showModal("noDelete");
        this.submitLoading = false;
        return;
      }
      if (this.me) await MemberService.deleteReservation(this.me.id, this.$route.params.id);
      this.$router.push({ name: "MyPage" });
    } catch (error: any) {
      this.submitLoading = false;
      throw new Error(error);
    }
  }

  private async submit() {
    try {
      if (this.me) {
        const isMember = await authCheck(this.me.id);
        if (!isMember) {
          Cognito.signOut();
          localStorage.removeItem("vuex");
          this.$router.push({ name: "Login" });
          return;
        }
      } else {
        localStorage.removeItem("vuex");
        this.$router.push({ name: "Login" });
        return;
      }
      this.submitLoading = true;
      if (this.reservationData?.status === "immutable") {
        this.showModal("error");
        this.submitLoading = false;
        return;
      }
      const isChangeable = await this.isChangeable(this.reservationData);
      if (!isChangeable) {
        this.closeModal("confirm");
        this.showModal("noChange");
        this.submitLoading = false;
        return;
      }
      const reservationId = this.$route.params.id;
      const updateReservationData = _.cloneDeep(this.reservationData);
      delete updateReservationData?.hospitalName;
      delete updateReservationData?.courseName;
      delete updateReservationData?.petName;
      if (updateReservationData) {
        if (this.pendingDate) updateReservationData.datetime = moment(this.pendingDate).format();
        updateReservationData.notification_status = "send";
        const payload = deleteNullProp(updateReservationData);
        delete payload.courseDetail;
        delete payload.status;
        delete payload.update_user;
        delete payload.is_message_required;
        if (!payload.memo) payload.memo = "";
        await MemberService.updateReservation(updateReservationData.member_id, reservationId, payload);
      }
      ReservationModule.changeSelectedNumberOfPets(1);
      this.$router.push({ name: "MyPage" });
    } catch (error: any) {
      if (error.response?.data?.error_msg) {
        this.modalErrorMessage = error.response.data.error_msg;
      } else {
        this.modalErrorMessage = "予約の更新中にエラーが発生しました。";
      }
      this.closeModal("confirm");
      this.showModal("error");
    } finally {
      this.submitLoading = false;
    }
  }

  private async showModalDelete() {
    if (this.me) {
      const isMember = await authCheck(this.me.id);
      if (!isMember) {
        Cognito.signOut();
        localStorage.removeItem("vuex");
        this.$router.push({ name: "Login" });
        return;
      }
    } else {
      localStorage.removeItem("vuex");
      this.$router.push({ name: "Login" });
      return;
    }
    if (this.reservationData?.status === "immutable") {
      this.showModal("error");
      return;
    }
    if (this.isChangeable(this.reservationData)) {
      this.showModal("delete");
    } else {
      this.showModal("noDelete");
    }
  }

  private showModal(name: string) {
    this.$modal.show(name);
  }

  private closeModal(name: string) {
    this.$modal.hide(name);
    if (name === "error") {
      this.modalErrorMessage = "エラーが発生しました。";
    }
  }

  private beforeDestroy() {
    ReservationModule.changeSelectedDate(null);
    ReservationModule.changeSelectedNumberOfPets(1);
  }
}
